import {useState, useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {apiService} from '../services/api';
import { normalizeDecimalSeparator, isNumericFieldType } from '../utils/numberInput';
import { datetimeLocalToISO, isoToDatetimeLocal, toTimeInput, timeInputToBackend, secondsToInterval, intervalInputToSeconds, normalizeIntervalDisplay } from '../utils/timestampUtils';

interface EditingCell {
    rowId: string | number;
    columnId: string;
}

interface UseCellEditReturn {
    editingCell: EditingCell | null;
    editValue: any;
    setEditValue: (value: any) => void;
    handleCellClick: (row: any, column: any, e: React.MouseEvent) => void;
    handleCellBlur: (screenId: string, queryKey: any[], visibleColumns: any[]) => void;
    handleDirectUpdate: (screenId: string, row: any, column: any, value: any, queryKey: any[]) => void;
    isEditing: (rowId: string | number, columnId: string) => boolean;
    isPending: boolean;
}

export const useCellEdit = (
    showSuccessCallback?: (message: string) => void,
    showErrorCallback?: (message: string) => void,
    /**
     * Set of screen IDs belonging to the current app. Used to scope the
     * post-edit broadcast invalidate so we only refetch cached queries for
     * screens of the app the user is currently in — not every screen from
     * every project they ever visited (which is what plain refetchType:'all'
     * across `['screenData', ...]` would do).
     */
    allowedScreenIds?: Set<string>
): UseCellEditReturn => {
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [editValue, setEditValue] = useState<any>('');
    const queryClient = useQueryClient();

    const updateCellMutation = useMutation({
        mutationFn: ({screenId, rowId, columnId, value}: {
            screenId: string;
            rowId: string | number;
            columnId: string;
            value: any
        }) =>
            apiService.updateColumnValue(screenId, rowId, columnId, value),
        onError: (error: any) => {
            if (showErrorCallback) {
                showErrorCallback(error.message || 'Chyba pri ukladaní');
            }
        }
    });

    const handleCellClick = useCallback((row: any, column: any, e: React.MouseEvent) => {
        if (!column.editable) {
            return;
        }

        e.stopPropagation();
        setEditingCell({rowId: row.id, columnId: column.id});

        // Convert timestamp to datetime-local format when editing starts
        if (column.columnType === 'timestamp') {
            setEditValue(isoToDatetimeLocal(row[column.code]));
        } else if (column.columnType === 'time') {
            // Convert time to HH:mm format when editing starts
            setEditValue(toTimeInput(row[column.code]));
        } else if (column.columnType === 'interval') {
            // Convert duration (seconds) to HH:MM when editing starts
            setEditValue(secondsToInterval(row[column.code]));
        } else {
            setEditValue(row[column.code] ?? '');
        }
    }, []);

    const handleCellBlur = useCallback((screenId: string, queryKey: any[], visibleColumns: any[]) => {
        if (!editingCell) {
            return;
        }

        const cachedData = queryClient.getQueryData(queryKey);

        if (!cachedData) {
            setEditingCell(null);
            return;
        }

        // Handle both paginated data structure {data: [...], totalPages, totalRecords} and plain array
        const isPaginated = typeof cachedData === 'object' && 'data' in cachedData && Array.isArray((cachedData as any).data);
        const dataArray: any[] = isPaginated ? (cachedData as any).data : (Array.isArray(cachedData) ? cachedData : []);

        if (dataArray.length === 0) {
            setEditingCell(null);
            return;
        }

        const row = dataArray.find((r: any) => r.id === editingCell.rowId);
        const column = visibleColumns.find((c: any) => c.id === editingCell.columnId);

        if (!row || !column) {
            setEditingCell(null);
            return;
        }

        const currentValue = row[column.code];

        // Normalize empty values to null for comparison
        const normalizeEmpty = (val: any) => {
            if (val === '' || val === null || val === undefined) return null;
            return val;
        };

        // Interval: editValue is HH:MM, stored value is seconds — compare in seconds
        if (column.columnType === 'interval') {
            const editSecs = intervalInputToSeconds(normalizeIntervalDisplay(editValue));
            const curSecs = normalizeEmpty(currentValue) === null ? null : Number(currentValue);
            if (editSecs === curSecs) {
                setEditingCell(null);
                return;
            }
        }

        const normalizedEditValue = normalizeEmpty(editValue);
        const normalizedCurrentValue = normalizeEmpty(currentValue);

        if (column.columnType !== 'interval' && String(normalizedEditValue) === String(normalizedCurrentValue)) {
            setEditingCell(null);
            return;
        }

        const previousData = cachedData;
        const rowId = editingCell.rowId;
        const columnId = editingCell.columnId;

        if (column.required && (editValue === '' || editValue === null || editValue === undefined)) {
            setEditingCell(null);
            if (showErrorCallback) {
                showErrorCallback('Toto pole je povinné');
            }
            return;
        }

        let valueToSend = editValue === '' ? null : editValue;

        if (valueToSend !== null && isNumericFieldType(column.columnType)) {
            valueToSend = normalizeDecimalSeparator(valueToSend);
        }

        // Convert timestamp from datetime-local format to ISO with timezone
        if (valueToSend !== null && column.columnType === 'timestamp') {
            valueToSend = datetimeLocalToISO(valueToSend);
        }

        // Convert time from HH:mm to HH:mm:ss format for backend
        if (valueToSend !== null && column.columnType === 'time') {
            valueToSend = timeInputToBackend(valueToSend);
        }

        // Convert duration HH:MM -> total seconds for backend (normalize unblurred input first)
        if (valueToSend !== null && column.columnType === 'interval') {
            valueToSend = intervalInputToSeconds(normalizeIntervalDisplay(valueToSend));
        }

        queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return old;

            // Handle paginated structure
            if (typeof old === 'object' && 'data' in old && Array.isArray(old.data)) {
                return {
                    ...old,
                    data: old.data.map((item: any) =>
                        item.id === rowId
                            ? {...item, [column.code]: valueToSend}
                            : item
                    )
                };
            }

            // Handle plain array
            if (Array.isArray(old)) {
                return old.map((item: any) =>
                    item.id === rowId
                        ? {...item, [column.code]: valueToSend}
                        : item
                );
            }

            return old;
        });

        updateCellMutation.mutate(
            {screenId, rowId, columnId, value: valueToSend},
            {
                onSuccess: (response) => {
                    if (!response.success) {
                        queryClient.setQueryData(queryKey, () => previousData);

                        let errorMessage = 'Chyba pri ukladaní';
                        if (response.errors) {
                            if (Array.isArray(response.errors) && response.errors.length > 0) {
                                errorMessage = response.errors.join(', ');
                            } else if (typeof response.errors === 'object') {
                                const allErrors: string[] = [];
                                Object.values(response.errors).forEach((fieldErrors) => {
                                    if (Array.isArray(fieldErrors)) {
                                        allErrors.push(...fieldErrors);
                                    }
                                });
                                if (allErrors.length > 0) {
                                    errorMessage = allErrors.join(', ');
                                }
                            }
                        } else if (response.message) {
                            errorMessage = response.message;
                        }

                        if (showErrorCallback) {
                            showErrorCallback(errorMessage);
                        }
                        return;
                    }

                    // The server answers with the raw database row (a many-to-one as a bare id, database
                    // names instead of column codes), not with a row of this screen — merging it blanked
                    // such cells. The edited list is read again instead, together with the other screens.
                    // refetchType: 'all' forces refetch even for inactive
                    // (unmounted) queries — e.g. the parent UpdateFormPage
                    // that's still in cache after navigating to a child
                    // screen. Scoped via predicate to screens of the current
                    // app, so cached queries from other projects the user
                    // visited earlier don't get re-fetched.
                    queryClient.invalidateQueries({
                        predicate: (q) => {
                            if (q.queryKey[0] !== 'screenData') return false;
                            if (JSON.stringify(q.queryKey) === JSON.stringify(queryKey)) return true;
                            if (!allowedScreenIds) return true;
                            return allowedScreenIds.has(String(q.queryKey[1]));
                        },
                        refetchType: 'all',
                    });
                    // An open dynamic form reads its record on its own; a row edited in its embedded table
                    // can change that record too (the total of a receipt), so it is read again.
                    queryClient.invalidateQueries({queryKey: ['dynamicFormData']});

                    if (showSuccessCallback) {
                        showSuccessCallback('Úspešne uložené');
                    }
                },
                onError: () => {
                    queryClient.setQueryData(queryKey, () => previousData);
                }
            }
        );
    }, [editingCell, editValue, queryClient, updateCellMutation, showSuccessCallback, showErrorCallback]);

    // Direct update for boolean fields - bypasses state-based flow
    const handleDirectUpdate = useCallback((
        screenId: string,
        row: any,
        column: any,
        value: any,
        queryKey: any[]
    ) => {
        const rowId = row.id;
        const columnId = column.id;
        const previousData = queryClient.getQueryData(queryKey);

        // Optimistic update
        queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return old;

            if (typeof old === 'object' && 'data' in old && Array.isArray(old.data)) {
                return {
                    ...old,
                    data: old.data.map((item: any) =>
                        item.id === rowId
                            ? {...item, [column.code]: value}
                            : item
                    )
                };
            }

            if (Array.isArray(old)) {
                return old.map((item: any) =>
                    item.id === rowId
                        ? {...item, [column.code]: value}
                        : item
                );
            }

            return old;
        });

        updateCellMutation.mutate(
            {screenId, rowId, columnId, value},
            {
                onSuccess: (response) => {
                    if (!response.success) {
                        queryClient.setQueryData(queryKey, () => previousData);

                        let errorMessage = 'Chyba pri ukladaní';
                        if (response.errors) {
                            if (Array.isArray(response.errors) && response.errors.length > 0) {
                                errorMessage = response.errors.join(', ');
                            } else if (typeof response.errors === 'object') {
                                const allErrors: string[] = [];
                                Object.values(response.errors).forEach((fieldErrors) => {
                                    if (Array.isArray(fieldErrors)) {
                                        allErrors.push(...fieldErrors);
                                    }
                                });
                                if (allErrors.length > 0) {
                                    errorMessage = allErrors.join(', ');
                                }
                            }
                        } else if (response.message) {
                            errorMessage = response.message;
                        }

                        if (showErrorCallback) {
                            showErrorCallback(errorMessage);
                        }
                        return;
                    }

                    // The raw database row of the answer is not a row of this screen (see handleCellBlur),
                    // so the edited list is read again instead of merging it.
                    // refetchType: 'all' forces refetch even for inactive
                    // (unmounted) queries — e.g. the parent UpdateFormPage
                    // that's still in cache after navigating to a child
                    // screen. Scoped via predicate to screens of the current
                    // app, so cached queries from other projects the user
                    // visited earlier don't get re-fetched.
                    queryClient.invalidateQueries({
                        predicate: (q) => {
                            if (q.queryKey[0] !== 'screenData') return false;
                            if (JSON.stringify(q.queryKey) === JSON.stringify(queryKey)) return true;
                            if (!allowedScreenIds) return true;
                            return allowedScreenIds.has(String(q.queryKey[1]));
                        },
                        refetchType: 'all',
                    });
                    // An open dynamic form reads its record on its own; a row edited in its embedded table
                    // can change that record too (the total of a receipt), so it is read again.
                    queryClient.invalidateQueries({queryKey: ['dynamicFormData']});

                    if (showSuccessCallback) {
                        showSuccessCallback('Úspešne uložené');
                    }
                },
                onError: () => {
                    queryClient.setQueryData(queryKey, () => previousData);
                }
            }
        );
    }, [queryClient, updateCellMutation, showSuccessCallback, showErrorCallback]);

    const isEditing = useCallback((rowId: string | number, columnId: string) => {
        return editingCell?.rowId === rowId && editingCell?.columnId === columnId;
    }, [editingCell]);

    return {
        editingCell,
        editValue,
        setEditValue,
        handleCellClick,
        handleCellBlur,
        handleDirectUpdate,
        isEditing,
        isPending: updateCellMutation.isPending
    };
};
